import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from '../components/GlassCard';
import FuturisticButton from '../components/FuturisticButton';

const {
  FiAlertTriangle,
  FiArrowRight,
  FiDatabase,
  FiPhone,
  FiShield
} = FiIcons;

const SECTIONS = [
  {
    title: 'Educational purpose only',
    items: [
      'MedExplainGPT provides automated explanations of report text for general educational purposes.',
      'It does not provide medical advice, diagnosis, prognosis, treatment, prescriptions, or emergency assessment.',
      'A qualified healthcare professional must interpret results using your symptoms, history, examination, and other clinical information.',
      'Do not start, stop, or change medication or treatment because of an AI-generated response.'
    ]
  },
  {
    title: 'AI and document-extraction limitations',
    items: [
      'AI output can be inaccurate, incomplete, misleading, outdated, or overly confident.',
      'OCR may misread numbers, units, names, symbols, reference ranges, or handwritten content.',
      'The system may miss context contained in charts, images, formatting, previous reports, or clinical notes.',
      'Compare the extracted-text preview with the original report and confirm every interpretation with your clinician.'
    ]
  },
  {
    title: 'Privacy and third-party processing',
    items: [
      'The site does not operate an application database for uploaded reports or generated explanations.',
      'The OpenAI API key is retained only in browser session storage and is removed when the tab session ends or when you clear it.',
      'Extracted medical-report text is transmitted directly from your browser to OpenAI using your API key.',
      'OpenAI processing is governed by your OpenAI account agreement, privacy terms, retention settings, and data controls.',
      'Remove direct identifiers such as names, addresses, Medicare numbers, dates of birth, and patient numbers before uploading whenever possible.',
      'No internet service can guarantee absolute confidentiality or security.'
    ]
  },
  {
    title: 'Australian users and legal limitations',
    items: [
      'The service is intended as a general informational tool for Australian users but does not claim regulatory approval as a medical device.',
      'References to Australian services or laws are general information and are not legal advice.',
      'Nothing in these terms excludes rights or remedies that cannot lawfully be excluded under Australian law, including applicable Australian Consumer Law guarantees.',
      'Statements about privacy practices describe the current application design and are not a certification of legal compliance.',
      'Organisations and healthcare providers should obtain independent privacy, security, medical-device, and legal advice before using the service professionally.'
    ]
  },
  {
    title: 'User responsibility',
    items: [
      'Only upload information you are authorised to process and do not upload another person’s report without appropriate authority.',
      'Use a private, trusted device and network, and protect your OpenAI API key.',
      'Verify important findings against the original report and discuss them with the issuing clinician.',
      'Stop using the service if you do not accept third-party processing by OpenAI.'
    ]
  }
];

const CONTACTS = [
  ['Emergency services', '000', 'Immediate danger or life-threatening emergencies'],
  ['healthdirect', '1800 022 222', '24-hour health information from a registered nurse'],
  ['Lifeline', '13 11 14', '24-hour crisis support and suicide prevention'],
  ['Poisons Information Centre', '13 11 26', '24-hour poisoning advice'],
  ['Beyond Blue', '1300 22 4636', 'Mental health information and support'],
  ['Kids Helpline', '1800 55 1800', 'Support for people aged 5 to 25']
];

const Disclaimer = () => (
  <div className="min-h-screen pt-24 sm:pt-28 pb-16 relative">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <SafeIcon icon={FiAlertTriangle} className="h-14 w-14 text-red-400 mx-auto mb-5" />
        <h1 className="text-3xl sm:text-5xl font-bold text-white terminal-text mb-4">
          Medical, Privacy and Legal Notice
        </h1>
        <p className="text-lg text-green-200">
          Important limitations and data-processing information for MedExplainGPT
        </p>
      </motion.header>

      <GlassCard className="p-5 sm:p-6 border-l-4 border-red-400 mb-8" hover={false}>
        <h2 className="text-xl font-bold text-red-400 mb-2">
          Do not use this service for emergencies
        </h2>
        <p className="text-white">
          Call 000 immediately for a medical emergency. AI output must never delay
          urgent care or replace advice from a qualified healthcare professional.
        </p>
      </GlassCard>

      <div className="space-y-6">
        {SECTIONS.map((section) => (
          <GlassCard key={section.title} className="p-5 sm:p-6" hover={false}>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {section.title}
            </h2>
            <ul className="space-y-3">
              {section.items.map((item) => (
                <li key={item} className="flex items-start text-green-100">
                  <span className="w-2 h-2 rounded-full bg-green-400 mt-2 mr-3 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-5 sm:p-6 mt-8 border border-yellow-400/30" hover={false}>
        <div className="flex items-center mb-4">
          <SafeIcon icon={FiDatabase} className="h-6 w-6 text-yellow-400 mr-3" />
          <h2 className="text-xl sm:text-2xl font-bold text-white">Data-flow summary</h2>
        </div>
        <ol className="space-y-2 text-green-100 list-decimal ml-5">
          <li>You select a document on your device.</li>
          <li>Text is extracted locally in the browser using PDF parsing or OCR.</li>
          <li>The extracted text and instructions are sent to OpenAI using your API key.</li>
          <li>The OpenAI response is displayed in the browser.</li>
          <li>The application does not save the report or result to an application database.</li>
        </ol>
      </GlassCard>

      <GlassCard className="p-5 sm:p-6 mt-8 border border-red-400/30" hover={false}>
        <div className="flex items-center mb-5">
          <SafeIcon icon={FiPhone} className="h-6 w-6 text-red-400 mr-3" />
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Australian support contacts
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONTACTS.map(([service, number, purpose]) => (
            <div key={service} className="p-4 rounded-xl bg-black/40 border border-red-400/20">
              <h3 className="text-red-300 font-semibold">{service}</h3>
              <a
                href={`tel:${number.replace(/\s/g, '')}`}
                className="block text-xl font-bold text-white my-2"
              >
                {number}
              </a>
              <p className="text-xs text-green-100">{purpose}</p>
            </div>
          ))}
        </div>
      </GlassCard>

      <GlassCard className="p-5 sm:p-6 mt-8" hover={false}>
        <div className="flex items-center mb-3">
          <SafeIcon icon={FiShield} className="h-6 w-6 text-green-400 mr-3" />
          <h2 className="text-xl font-bold text-white">Professional legal review</h2>
        </div>
        <p className="text-green-100">
          This notice improves transparency but is not a substitute for advice from an
          Australian lawyer, privacy professional, cybersecurity specialist, or medical-device
          regulatory adviser. The operator should obtain professional review before commercial
          launch, clinical promotion, or processing reports on behalf of other people.
        </p>
      </GlassCard>

      <div className="max-w-sm mx-auto mt-8">
        <FuturisticButton as={Link} to="/upload" size="lg" icon={FiArrowRight}>
          Continue to Upload
        </FuturisticButton>
      </div>
    </div>
  </div>
);

export default Disclaimer;